import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { InputTextarea } from "primereact/inputtextarea";
import {
	Button,
	Col,
	Container,
	Form,
	OverlayTrigger,
	Row,
	Spinner,
	Tooltip,
} from "react-bootstrap";
import Error from "../../../../components/Error";
import { ContentBody, MultiSelectFilter } from "../../styled";

import { MdInfoOutline } from "react-icons/md";
import {
	getCourse,
	newCourse,
	updateCourse,
} from "../../../../services/manager/admin/courses.service";
import { toast } from "react-toastify";
import { HiOutlineArrowCircleLeft } from "react-icons/hi";
import { listKnowledges } from "../../../../services/filters.service";

const validationSchema = Yup.object({
	name: Yup.string().trim().required("Este campo é obrigatório"),
	modality: Yup.number().required("Este campo é obrigatório"),
	local: Yup.array().required("Este campo é obrigatório"),
	description: Yup.string().max(150, "Limite máximo de 150 caracteres"),
}).required();

export const NewEditCourse = (): JSX.Element => {
	const history = useHistory();
	const { t: translation } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const {
		register,
		handleSubmit,
		watch,
		setValue,
		setError,
		clearErrors,
		formState: { errors, isDirty },
	} = useForm({ resolver: yupResolver(validationSchema) });

	const [loading, setLoading] = useState<boolean>(false);
	const [loadingButton, setLoadingButton] = useState<boolean>(false);
	const [count, setCount] = useState<number>(0);
	const [courseName, setCourseName] = useState<string>("");
	const [modality, setModality] = useState<any>();
	const [local, setLocal] = useState<any>();
	const [courseKnowledgeArea, setCourseKnowledgeArea] = useState<any>();

	const watchDescription = watch("description", false);

	const modalityOptions = [
		{ label: "Graduação", value: 1 },
		{ label: "Pós-graduação", value: 2 },
		{ label: "Livre", value: 3 },
		{ label: "Técnico", value: 4 },
	];

	const localOptions = [
		{ label: "Locação", value: "locacao" },
		{ label: "Loja", value: "loja" },
	];

	const [knowledgeOptions, setKnowledgeOptions] = useState([]);

	const onSubmit = (formData: any) => {
		console.log(formData);

		setLoadingButton(true);

		if (formData.local.length === 0) {
			setError("local", {
				type: "custom",
				message: "Este campo é obrigatório",
			});
			setLoadingButton(false);
			return;
		}

		let obj = {
			name: formData.name,
			description: formData.description,
			type_course: formData.modality,
			is_location: formData.local.includes("locacao"),
			is_store: formData.local.includes("loja"),
			knowledge_area: formData.knowledge,
		};

		if (id) {
			updateCourse(id, obj)
				.then(() => {
					toast.success("Alterações salvas com sucesso!");
					history.goBack();
				})
				.catch((err: any) => {
					toast.error(
						"Ocorreu um erro ao tentar atualizar este curso. Tente novamente."
					);
				})
				.finally(() => {
					setLoadingButton(false);
				});
		} else {
			newCourse(obj)
				.then(() => {
					toast.success("Curso criado com sucesso!");
					history.goBack();
				})
				.catch((err: any) => {
					toast.error(
						"Ocorreu um erro ao tentar criar este curso. Tente novamente."
					);
				})
				.finally(() => {
					setLoadingButton(false);
				});
		}
	};

	useEffect(() => {
		if (watchDescription) {
			setCount(watchDescription.length);
		}
	}, [watchDescription]);

	useEffect(() => {
		/** Carrega as áreas de conhecimento disponíveis e seta o state de opções */
		listKnowledges().then((data) => {
			setKnowledgeOptions(
				data.map((anArea: any) => {
					return { id: anArea.id, description: anArea.description };
				})
			);
		});

		/** Carrega infos do curso que estou editando - caso esteja editando um curso */
		if (id) {
			setLoading(true);
			getCourse(id)
				.then((course: any) => {
					let courseLocations: any = [];

					if (course.is_location) {
						courseLocations.push("locacao");
					}

					if (course.is_store) {
						courseLocations.push("loja");
					}

					const knowledgeAreas =
						course.knowledge_area?.length > 0
							? course.knowledge_area.map(
									(anArea: any) => anArea.id
							  )
							: [];

					// set states locais
					setModality(course.type_course);
					setLocal(courseLocations);
					setCourseName(course.name);
					setCourseKnowledgeArea(knowledgeAreas);

					// set valores do form
					setValue("name", course.name);
					setValue("modality", course.type_course);
					setValue("local", courseLocations);
					setValue("description", course.description);
					setValue("knowledge", knowledgeAreas);
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, []);

	if (loading) {
		return (
			<div className="loading-container mt-5">
				<Spinner
					animation="border"
					variant="primary"
				/>
			</div>
		);
	}

	return (
		<Container fluid>
			<Row>
				<Col>
					<h4>
						<OverlayTrigger
							placement={"top"}
							overlay={<Tooltip>Voltar</Tooltip>}
						>
							<span>
								<HiOutlineArrowCircleLeft
									size={25}
									style={{
										marginRight: "10px",
										marginBottom: "6px",
										cursor: "pointer",
									}}
									onClick={() => history.goBack()}
								/>
							</span>
						</OverlayTrigger>
						<b>{id ? courseName + " / Editar" : "Novo curso"}</b>
					</h4>

					<p className="mb-4 text-black font-light">
						{id
							? "Altere as informações referentes ao seu curso."
							: "Crie uma matriz curricular com base nas disciplinas que estiverem cadastradas na DStore."}
					</p>
				</Col>
			</Row>
			<ContentBody>
				<Form onSubmit={handleSubmit(onSubmit)}>
					<Row>
						<Col
							xs={12}
							md={9}
							lg={7}
							xl={7}
							xxl={4}
						>
							{/* ------- *** -------- */}
							{/*    Nome do curso     */}
							{/* ------- *** -------- */}
							<div className="mb-4">
								<label className="block">Nome</label>
								<InputText
									{...register("name", { required: true })}
									type="text"
									className="w-100"
									style={{ height: "40px" }}
								/>
								{errors.name ? (
									<div className="w-100">
										{" "}
										<Error>
											{errors.name?.message}
										</Error>{" "}
									</div>
								) : (
									""
								)}
							</div>
							{/* ------- *** -------- */}
							{/*      Modalidade      */}
							{/* ------- *** -------- */}
							<div className="mb-4">
								<label className="block">Modalidade</label>
								<Dropdown
									className="w-100 select-form-default"
									options={modalityOptions}
									value={modality}
									onChange={(e) => {
										setModality(e.value);
										setValue("modality", e.value);
									}}
								/>
								{errors.modality ? (
									<div className="w-100">
										{" "}
										<Error>
											{errors.modality?.message}
										</Error>{" "}
									</div>
								) : (
									""
								)}
							</div>
							{/* ------- *** -------- */}
							{/* Local - Loja, Locacao */}
							{/* ------- *** -------- */}
							<div className="mb-4">
								<label
									htmlFor="name"
									className="block"
								>
									Local
									<OverlayTrigger
										placement={"right"}
										overlay={
											<Tooltip>
												Selecione o local onde deseja
												exibir o curso
											</Tooltip>
										}
									>
										<span className="ml-1">
											<MdInfoOutline />
										</span>
									</OverlayTrigger>
								</label>

								<MultiSelectFilter>
									<MultiSelect
										value={local}
										filter
										className="multiselect-dropdown ds-select w-100"
										style={{ border: "1px solid #ced4da" }}
										options={localOptions}
										onChange={(event: any) => {
											setLocal(event.value);
											setValue("local", event.value);
											clearErrors("local");
										}}
									/>
								</MultiSelectFilter>
								{errors.local ? (
									<div className="w-100">
										{" "}
										<Error>
											{errors.local?.message}
										</Error>{" "}
									</div>
								) : (
									""
								)}
							</div>
							{/* ------- *** -------- */}
							{/* Área de conhecimento do curso - opcional */}
							{/* ------- *** -------- */}
							<div className="mb-4">
								<label
									htmlFor="select-knowledge-area"
									className="block"
								>
									Área de Conhecimento
									<OverlayTrigger
										placement={"right"}
										overlay={
											<Tooltip>
												Selecione a(s) área(s) de
												conhecimento do curso
											</Tooltip>
										}
									>
										<span className="ml-1">
											<MdInfoOutline />
										</span>
									</OverlayTrigger>
								</label>

								<MultiSelectFilter>
									<MultiSelect
										id="select-knowledge-area"
										value={courseKnowledgeArea}
										optionLabel="description"
										optionValue="id"
										className="multiselect-dropdown ds-select w-100"
										style={{ border: "1px solid #ced4da" }}
										options={knowledgeOptions}
										onChange={(event: any) => {
											setCourseKnowledgeArea(event.value);
											setValue("knowledge", event.value);
										}}
										filter
									/>
								</MultiSelectFilter>
							</div>
							{/* ------- *** -------- */}
							{/* Descrição do curso - opcional */}
							{/* ------- *** -------- */}
							<div className="mb-4">
								<label
									htmlFor="description"
									className="block"
								>
									Descrição{" "}
									<span className="text-grey">
										(Opcional)
									</span>
								</label>
								<InputTextarea
									className="w-100"
									rows={5}
									cols={30}
									{...register("description", {
										minLength: 1,
										maxLength: 150,
									})}
									autoResize
								/>
								{count > 0 && (
									<p className="font-13 text-grey text-end mb-1">
										{count} / 150
									</p>
								)}
								{errors.description ? (
									<div className="w-100">
										{" "}
										<Error>
											{errors.description?.message}
										</Error>{" "}
									</div>
								) : (
									""
								)}
							</div>

							<div className="text-end mb-3">
								{loadingButton ? (
									<Spinner
										animation="border"
										variant="primary"
										style={{
											width: "2rem",
											height: "2rem",
											borderWidth: "0.25em",
											borderRightColor: "transparent",
										}}
									/>
								) : (
									<>
										<Button
											variant="outline-primary mr-2"
											onClick={() => history.goBack()}
										>
											<span className="ml-3 mr-3">
												{translation("cancel")}
											</span>
										</Button>
										{/* ------ */}
										{/* Submit - Criar ou Salvar */}
										{/* ------ */}
										<Button
											type="submit"
											data-testid="submit"
										>
											<span className="ml-3 mr-3">
												{id
													? translation(
															"manager.settings.save"
													  )
													: translation(
															"manager.settings.saveNew"
													  )}
											</span>
										</Button>
									</>
								)}
							</div>
						</Col>
					</Row>
				</Form>
			</ContentBody>
		</Container>
	);
};
